// Dual-hat accounts (PRD Epic E): a user with both a staff hat (workspace
// membership / manager role) and a resident hat (unit role -
// surfaced by the backend as `has_home_mode` on /auth/me) can switch
// between "Manage" and "My home". The last-used mode is remembered per device
// per user (like the property selection); first login defaults to My home.

import { STAFF_ROLES, type RoleLikeUser } from '@/lib/user-roles';

export type ResidentMode = 'manage' | 'home';

export const RESIDENT_MODE_STORAGE_PREFIX = 'koinon_resident_mode_';

export interface MembershipLike {
  role?: string | null;
}

// Workspace memberships that grant a staff/PM hat. Directory-style
// 'resident' memberships are community links, never a manage hat.
const STAFF_MEMBERSHIP_ROLES = new Set(['owner', 'manager', 'auditor']);

export const modeStorageKey = (userId: string): string =>
  `${RESIDENT_MODE_STORAGE_PREFIX}${userId}`;

export function isSuperuserLike(user?: RoleLikeUser | null): boolean {
  return Boolean(user && user.role === 'superadmin');
}

/** True when the account holds a staff hat (a workspace membership with a
 * staff role, or a manager/auditor platform role). Pure portal users have
 * none - resident-role memberships don't count. */
export function hasStaffHat(
  user?: RoleLikeUser | null,
  memberships?: MembershipLike[] | null
): boolean {
  if (!user || isSuperuserLike(user)) return false;
  if (STAFF_ROLES.has(user.role ?? '')) return true;
  return Boolean(
    memberships && memberships.some((m) => STAFF_MEMBERSHIP_ROLES.has(m?.role ?? ''))
  );
}

/** True when the account holds a resident hat: any unit role
 * anywhere (backend `has_home_mode` on /auth/me). */
export function hasResidentHat(user?: RoleLikeUser | null): boolean {
  return Boolean(user && user.has_home_mode === true);
}

/** Dual-hat = staff hat AND resident hat. Single-hat accounts never see the
 * Manage / My home switch. */
export function isDualHat(
  user?: RoleLikeUser | null,
  memberships?: MembershipLike[] | null
): boolean {
  return Boolean(
    user && !isSuperuserLike(user) && hasStaffHat(user, memberships) && hasResidentHat(user)
  );
}

export function readStoredMode(userId?: string): ResidentMode | null {
  if (!userId) return null;
  const stored = localStorage.getItem(modeStorageKey(userId));
  return stored === 'manage' || stored === 'home' ? stored : null;
}

export function writeStoredMode(userId: string, mode: ResidentMode): void {
  localStorage.setItem(modeStorageKey(userId), mode);
}
